import useSWR from "swr"
import { RoleEnum } from "common/types/graphql"
import { createContext, useContext, useSyncExternalStore } from "react"
import { hasActiveSession } from "common/lib/cookies"
import { CurrentUserDonations, fetchCurrentUser } from "common/lib/fetchCurrentUser"

const VIEWER_URL = "/users/current.json?include=counts"

type Viewer = {
  role: RoleEnum
  user: {
    id: string
    name: string
    avatar: string
    commentNotificationCount: number
    newsFeedCount: number
    allow: { analytics: boolean; moderateComments: boolean; videoWithMetadata: boolean }
    donations: CurrentUserDonations
  } | null
}

/** @internal Knip: exported for tests */
export const TestViewerContext = createContext<Viewer | undefined>(undefined)

// The API returns this for viewers with no session, so anonymous users see
// the same shape whether or not we skip the request. The reference is stable
// so effects that depend on `viewer` don't re-run on every parent render.
const ANONYMOUS_VIEWER: Viewer = { role: RoleEnum.Reader, user: null }

// Mapping in the fetcher lets SWR cache the viewer, so its reference stays stable too.
const fetchViewer = async (url: string): Promise<Viewer> => {
  const currentUser = await fetchCurrentUser(url)
  if (currentUser.id === undefined) return ANONYMOUS_VIEWER

  return {
    role: currentUser.type,
    user: {
      id: String(currentUser.id),
      name: currentUser.full_name,
      avatar: currentUser.avatar_url,
      commentNotificationCount: currentUser.comment_notification_count ?? 0,
      newsFeedCount: currentUser.news_feed_count ?? 0,
      allow: {
        analytics: currentUser.allow.analytics,
        moderateComments: currentUser.allow.moderate_comments,
        videoWithMetadata: currentUser.allow.video_with_metadata,
      },
      donations: currentUser.donations,
    },
  }
}

// Cookies have no change notification, and we only need the value once
// per page load.
const subscribeToSession = () => () => {}

/**
 * Data hook to return the current viewer.
 * This hook also implements helper attributes to determine viewer roles.
 */
export const useViewer = () => {
  // `hasActiveSession` reads `document.cookie`, which the server doesn't have.
  // Returning null from the server snapshot keeps the first client render
  // identical to the server render, so signed-in users don't trip a hydration
  // mismatch.
  const sessionActive = useSyncExternalStore<boolean | null>(
    subscribeToSession,
    hasActiveSession,
    () => null,
  )

  const { data, error } = useSWR(sessionActive === true ? VIEWER_URL : null, fetchViewer)

  const testViewer = useContext(TestViewerContext)
  const anonymousViewer = sessionActive === false ? ANONYMOUS_VIEWER : undefined
  const viewer = testViewer || data || anonymousViewer

  return {
    viewer,
    // A failed request counts as loaded so pages render as signed out while SWR retries.
    loaded: sessionActive === false || !!data || !!error,
    isSignedIn: !!viewer?.user,
    isEditor: viewer?.role === RoleEnum.Editor,
    isAuthor: viewer?.role === RoleEnum.Author,
    isReader: viewer?.role === RoleEnum.Reader,
  }
}
